import { ArrowLeft, Heart, Share2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AISummarySheet } from "@/components/AISummarySheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

interface ContentBlock {
  type: "text" | "image";
  content: string;
}

interface ArticleDetailProps {
  image: string;
  title: string;
  description: string;
  views: number;
  liked: boolean;
  content?: ContentBlock[];
  isAdmin?: boolean;
  articleId?: string;
  onLike: () => void;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const ArticleDetail = ({ 
  image, 
  title, 
  description, 
  views, 
  liked, 
  content,
  isAdmin,
  articleId,
  onLike, 
  onClose,
  onEdit,
  onDelete
}: ArticleDetailProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleShare = async () => {
    const shareData = {
      title: title,
      text: description.substring(0, 100) + "...",
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Articolo condiviso!");
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copiato negli appunti!");
      }
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(window.location.href);
          toast.success("Link copiato negli appunti!");
        } catch {
          toast.error("Impossibile condividere l'articolo");
        }
      }
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(false);
    onDelete?.();
  };

  return (
    <>
      <div data-tecly-article-detail="true" className="fixed inset-0 z-50 bg-background overflow-y-auto tecly-enter">
        {/* Header with back button, share, and like */}
        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <button 
              onClick={onClose}
              aria-label="Indietro"
              className="lg-icon-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <button 
                    onClick={onEdit}
                    className="p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
                    title="Modifica articolo"
                  >
                    <Pencil className="w-5 h-5 text-foreground" />
                  </button>
                  <button 
                    onClick={() => setShowDeleteDialog(true)}
                    className="p-2 rounded-full hover:bg-destructive/20 transition-all duration-300 hover:scale-110 active:scale-90"
                    title="Elimina articolo"
                  >
                    <Trash2 className="w-5 h-5 text-destructive" />
                  </button>
                </>
              )}
              <button 
                onClick={handleShare}
                className="p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
              >
                <Share2 className="w-5 h-5 text-foreground" />
              </button>
              <button 
                onClick={onLike}
                className="p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
              >
                <Heart 
                  className={`w-6 h-6 transition-all duration-300 ${
                    liked 
                      ? "fill-red-500 text-red-500 scale-110" 
                      : "text-foreground hover:text-red-400"
                  }`} 
                />
              </button>
            </div>
          </div>
        </div>

        {/* Cover Image */}
        <div className="container mx-auto px-4 pt-6">
          <div className="w-full h-[200px] md:h-[280px] overflow-hidden rounded-2xl">
            <img 
              src={image} 
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex-1">
              {title}
            </h1>
            {articleId && (
              <AISummarySheet
                articleId={articleId}
                articleTitle={title}
                articleDescription={description}
              />
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
            <span>{views.toLocaleString('it-IT')} visualizzazioni</span>
          </div>

          <div className="prose prose-lg max-w-none text-foreground/90 leading-relaxed text-base md:text-lg whitespace-pre-line mb-8">
            {description}
          </div>

          {/* Content blocks */}
          {content && content.length > 0 && (
            <div className="space-y-6">
              {content.map((block, index) => (
                <div key={index}>
                  {block.type === "text" ? (
                    <p className="text-foreground/90 leading-relaxed text-base md:text-lg whitespace-pre-line">
                      {block.content}
                    </p>
                  ) : (
                    <div className="rounded-xl overflow-hidden">
                      <img 
                        src={block.content} 
                        alt={`Contenuto ${index + 1}`}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo articolo?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{title}". Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
